import React, {Suspense, useRef, useState} from "react";
import {useMessages} from "i18n";
import {useIsLoggedIn} from "UserProvider";
import {ContactsIcon} from "./icons";
import {FromContactsButton} from "./ContactsRecipientPicker.styles";
import {focusOpener} from "components/sheetOpener";
import type {GroupResponse} from "../../contacts/groups/GroupApi";

// Lazy so the whole contacts machinery (SelectableContactsList → useContacts → ContactsApi) lands in
// its OWN chunk instead of the eager home bundle — the panel only pays for it once a logged-in user
// actually opens the picker (ui#166).
const ContactsPickerDialog = React.lazy(() => import("./ContactsPickerDialog"));
const GroupsPickerDialog = React.lazy(() => import("./GroupsPickerDialog"));

export type ContactsRecipientPickerProps = {
	/** Receives the picked contacts' emails; the hook merges them (dedup + cap) into recipients. */
	onConfirm: (emails: string[]) => void;
	/** Receives the confirmed group selection; the hook merges it (dedup + cap) alongside the emails. */
	onConfirmGroups: (groups: GroupResponse[]) => boolean;
	selectedGroups: GroupResponse[];
	recipientError: string | null;
};

/**
 * The saved-contacts + saved-groups entry points for the unified recipients field (ui#166, ui#210).
 * Both affordances live here — the panel no longer switches a recipient "mode"; the two picker dialogs
 * feed the SAME field (email chips and group chips coexist). Rendered only for a logged-in user via
 * {@link useIsLoggedIn} — which is `false` on the server AND the first client render, so it never alters
 * the pre-rendered logged-out HTML (hydration-safe, same discipline as the sender prefill). Each heavy
 * dialog mounts lazily on first open and then stays mounted so BaseDialog's sheet transition can play
 * on both enter and exit.
 */
export function ContactsRecipientPicker({
	onConfirm,
	onConfirmGroups,
	selectedGroups,
	recipientError,
}: ContactsRecipientPickerProps) {
	const {sender} = useMessages();
	const loggedIn = useIsLoggedIn();
	const [open, setOpen] = useState(false);
	const [mounted, setMounted] = useState(false);
	const contactsTriggerRef = useRef<HTMLButtonElement>(null);
	const [groupsOpen, setGroupsOpen] = useState(false);
	const [groupsMounted, setGroupsMounted] = useState(false);
	const groupTriggerRef = useRef<HTMLButtonElement>(null);

	if (!loggedIn) {
		return null;
	}

	const openPicker = () => {
		setMounted(true);
		setOpen(true);
	};
	const openGroupsPicker = () => {
		setGroupsMounted(true);
		setGroupsOpen(true);
	};
	const closePicker = () => setOpen(false);
	const closeGroupsPicker = () => setGroupsOpen(false);
	const restoreContactsTrigger = () => focusOpener(contactsTriggerRef.current);
	const restoreGroupsTrigger = () => focusOpener(groupTriggerRef.current);

	return (
		<>
			<FromContactsButton ref={contactsTriggerRef} type="button" onClick={openPicker}>
				<ContactsIcon size={16}/> {sender.recipients.fromContacts}
			</FromContactsButton>
			<FromContactsButton ref={groupTriggerRef} type="button" onClick={openGroupsPicker}>
				<ContactsIcon size={16}/> {sender.recipients.fromGroups}
			</FromContactsButton>
			{mounted && (
				<Suspense fallback={null}>
					{/* This exit callback is the sole owner; BaseDialog's MUI trap restore is disabled. */}
					<ContactsPickerDialog
						open={open}
						onClose={closePicker}
						onConfirm={onConfirm}
						onTransitionExited={restoreContactsTrigger}
					/>
				</Suspense>
			)}
			{groupsMounted && (
				<Suspense fallback={null}>
					{/* This exit callback is the sole owner; BaseDialog's MUI trap restore is disabled. */}
					<GroupsPickerDialog
						open={groupsOpen}
						selectedGroups={selectedGroups}
						error={recipientError}
						onClose={closeGroupsPicker}
						onConfirm={onConfirmGroups}
						onTransitionExited={restoreGroupsTrigger}
					/>
				</Suspense>
			)}
		</>
	);
}
